/**
 * BIRHAUS Style Variant Utilities
 * 
 * Exports styling functions that developers can use to create
 * custom components compatible with BIRHAUS design system.
 * 
 * Usage:
 * ```ts
 * import { buttonVariants, inputVariants } from '@birhaus/primitives'
 * 
 * const MyCustomButton = ({ variant, size, className }) => (
 *   <button className={buttonVariants({ variant, size, className })}>
 *     Content
 *   </button>
 * )
 * ```
 */

// Utility function for class name composition
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Button variant utility
export interface ButtonVariantProps {
  variant?: 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  isPrimaryAction?: boolean
  fullWidth?: boolean
  className?: string
}

export function buttonVariants({
  variant = 'secondary',
  size = 'md',
  isPrimaryAction = false,
  fullWidth = false,
  className
}: ButtonVariantProps = {}): string {
  // Base classes for all buttons
  const baseClasses = cn(
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'data-[birhaus-component]:border-0', // BIRHAUS compliance
    fullWidth && 'w-full'
  )

  // Size classes
  const sizeClasses = {
    xs: 'h-7 px-2 text-xs',
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-11 px-6 text-base',
    xl: 'h-12 px-8 text-base'
  }

  // Variant classes with primary action enhancement
  const variantClasses = {
    primary: isPrimaryAction 
      ? 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 shadow-md hover:shadow-lg'
      : 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
    secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-500',
    destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
    outline: 'border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-500',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-500'
  }

  return cn(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    className
  )
}

// Input variant utility
export interface InputVariantProps {
  variant?: 'default' | 'filled' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  error?: boolean
  success?: boolean
  className?: string
}

export function inputVariants({
  variant = 'default',
  size = 'md',
  error = false,
  success = false,
  className
}: InputVariantProps = {}): string {
  // Base classes for all inputs
  const baseClasses = cn(
    'w-full rounded-lg border transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
    'placeholder:text-gray-400'
  )

  // Size classes
  const sizeClasses = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-10 px-3 text-sm',
    lg: 'h-11 px-4 text-base'
  }

  // Variant classes
  const variantClasses = {
    default: 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500',
    filled: 'border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
    minimal: 'border-transparent bg-transparent border-b-2 border-b-gray-300 rounded-none focus:border-b-blue-500 focus:ring-0'
  }

  // State classes
  const stateClasses = cn(
    error && 'border-red-300 focus:border-red-500 focus:ring-red-500',
    success && 'border-green-300 focus:border-green-500 focus:ring-green-500'
  )

  return cn(
    baseClasses,
    sizeClasses[size],
    !error && !success && variantClasses[variant],
    stateClasses,
    className
  )
}

// Card variant utility
export interface CardVariantProps {
  variant?: 'default' | 'outline' | 'filled' | 'elevated'
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

export function cardVariants({
  variant = 'default',
  padding = 'md',
  className
}: CardVariantProps = {}): string {
  // Base classes for all cards
  const baseClasses = 'rounded-lg transition-all duration-200'

  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8'
  }

  // Variant classes
  const variantClasses = {
    default: 'bg-white border border-gray-200',
    outline: 'bg-transparent border border-gray-300',
    filled: 'bg-gray-50 border border-gray-200',
    elevated: 'bg-white border border-gray-200 shadow-sm hover:shadow-md'
  }

  return cn(
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    className
  )
}

// Badge variant utility
export interface BadgeVariantProps {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function badgeVariants({
  variant = 'default',
  size = 'md',
  className
}: BadgeVariantProps = {}): string {
  // Base classes for all badges
  const baseClasses = 'inline-flex items-center rounded-full font-medium'

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-sm'
  }

  // Variant classes
  const variantClasses = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800'
  }

  return cn(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    className
  )
}

// Alert variant utility
export interface AlertVariantProps {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
  className?: string
}

export function alertVariants({
  variant = 'default',
  className
}: AlertVariantProps = {}): string {
  // Base classes for all alerts
  const baseClasses = 'rounded-lg border p-4'

  // Variant classes
  const variantClasses = {
    default: 'bg-gray-50 border-gray-200 text-gray-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  }

  return cn(
    baseClasses,
    variantClasses[variant],
    className
  )
}

// Status badge utility (for status-specific styling)
export interface StatusBadgeVariantProps {
  status: 'active' | 'inactive' | 'pending' | 'approved' | 'rejected' | 'draft' | 'published'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function statusBadgeVariants({
  status,
  size = 'md',
  className
}: StatusBadgeVariantProps): string {
  // Base classes
  const baseClasses = 'inline-flex items-center rounded-full font-medium'

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-sm'
  }

  // Status-specific classes (BIRHAUS-compliant colors)
  const statusClasses = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    draft: 'bg-gray-100 text-gray-800',
    published: 'bg-blue-100 text-blue-800'
  }

  return cn(
    baseClasses,
    sizeClasses[size],
    statusClasses[status],
    className
  )
}

// Export all variant functions
export const variants = {
  button: buttonVariants,
  input: inputVariants,
  card: cardVariants,
  badge: badgeVariants,
  alert: alertVariants,
  statusBadge: statusBadgeVariants
}

// Types are exported above as interfaces